import { createAction, props } from '@ngrx/store';
import { PersistedTimestamps } from '../date-manipulation-check-collection.state';

/**
 * Date-manipulation-check store actions.
 *
 * Payloads are restricted to plain strings/booleans (state-shaped, never `moment.Moment`) so
 * they pass the root store's `strictActionSerializability` check. Moment conversion stays at the
 * `DateManipulationCheckNgrxService` boundary via `date-manipulation-check.converters.ts`; only
 * serializable state flows through these actions.
 *
 * `correlationId` on `externalTimestampReceived` lets `date-manipulation-check-write-bridge.ts`
 * match a settle action back to the specific write it dispatched, so the returned observable
 * completes (or errors) for that write rather than for a concurrent one. It is optional because
 * the same action is also dispatched internally by `fetchExternalTimestamp$` on init, which has
 * no caller waiting on a settle outcome.
 */

// --- Lifecycle ---------------------------------------------------------------

/** Dispatched by the service's `init()` to start the hydrate → fetch → check chain. */
export const initDateManipulationCheck = createAction('[DateManipulationCheck] Init');

/**
 * Dispatched by the hydrate effect once the persisted timestamps have been read. The reducer
 * absorbs the legacy `stored ?? now` / `stored ?? invalid` defaulting from here.
 */
export const dateManipulationCheckTimestampsRestoredFromStorage = createAction(
    '[DateManipulationCheck] Timestamps restored from storage',
    props<{ timestamps: PersistedTimestamps }>(),
);

/** Dispatched once the init chain has settled — see the effect's doc comment for the caveats. */
export const dateManipulationCheckLoaded = createAction('[DateManipulationCheck] Loaded');

/** Dispatched when the `IsaliveSet` fetch fails. */
export const dateManipulationCheckFailed = createAction('[DateManipulationCheck] Failed', props<{ error: string }>());

// --- Mutations -----------------------------------------------------------------

/**
 * Records a timestamp received from the trusted gateway as the new external baseline. Dispatched
 * both by `fetchExternalTimestamp$` on init (no `correlationId`) and by the service's
 * `setExternalTimestamp()` (with one, for the write-bridge settle).
 */
export const externalTimestampReceived = createAction(
    '[DateManipulationCheck] External timestamp received',
    props<{ timestamp: string; correlationId?: string }>(),
);

/**
 * Requests a validity check of `timestamp` against the last valid internal/external timestamps.
 * Dispatched by the service's `checkTimestamp()`. Shares `dateManipulationCheckTimestampReducer`
 * with {@link initialTimestampCheckRequested} — see the reducer's doc comment for why the policy
 * is one handler behind two actions.
 */
export const timestampCheckRequested = createAction(
    '[DateManipulationCheck] Timestamp check requested',
    props<{ timestamp: string }>(),
);

/**
 * The init-flow counterpart of {@link timestampCheckRequested}, dispatched **only** by
 * `fetchExternalTimestamp$` once the external timestamp has been fetched. Kept as a separate
 * action (rather than a flag on the same action) so `completeInit$` can react to the init check
 * and only the init check.
 */
export const initialTimestampCheckRequested = createAction(
    '[DateManipulationCheck] Initial timestamp check requested',
    props<{ timestamp: string }>(),
);

// --- Settle ---------------------------------------------------------------------

/**
 * Settle-only signal that `setExternalTimestamp()`'s write was persisted successfully (mapped
 * from the generic `slicePersisted`). The store is already the source of truth, so this carries
 * no timestamp payload — it only lets `date-manipulation-check-write-bridge.ts` complete for the
 * matching `correlationId`.
 */
export const externalTimestampPersisted = createAction(
    '[DateManipulationCheck] External timestamp persisted',
    props<{ correlationId?: string }>(),
);

export const externalTimestampPersistError = createAction(
    '[DateManipulationCheck] External timestamp persist error',
    props<{ error: string; correlationId?: string }>(),
);
